
import { Button } from "@/components/ui/button";
import { Gift, Loader2 } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "@supabase/auth-helpers-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface DonationButtonProps {
  priceId: string;
  price: number;
  variant?: "default" | "secondary" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  isCompact?: boolean;
}

export function DonationButton({ 
  priceId, 
  price, 
  variant = "secondary", 
  size = "sm",
  isCompact = false 
}: DonationButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { toast } = useToast();
  const session = useSession();
  const navigate = useNavigate();

  const handleDonation = async () => {
    // Reset error state
    setErrorMessage(null);
    
    try {
      setIsLoading(true);
      
      // Make the request to the create-checkout function
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId },
        // Include auth token if user is logged in, otherwise proceed without it
        headers: session?.access_token 
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined
      });

      if (error) {
        console.error('Checkout error from Supabase:', error);
        
        // Display a more helpful error message
        let errorMsg = 'Unable to process donation. Please try again later.';
        if (error.message.includes('404')) {
          errorMsg = 'The checkout service is currently unavailable. Please try again later.';
        } else if (error.message.includes('401')) {
          errorMsg = 'Authentication error. Please try again.';
        }
        
        setErrorMessage(errorMsg);
        toast({
          title: "Checkout Error",
          description: errorMsg,
          variant: "destructive",
        });
        return;
      }

      if (!data?.url) {
        let errorMsg = "Invalid checkout response. Please try again.";
        if (data?.error) {
          errorMsg = data.error;
          console.error('Error from create-checkout function:', data.error);
          
          // Provide better context for the error
          if (data.error.includes('price') || data.error.includes('Price')) {
            errorMsg = "The selected donation option is currently unavailable. Please try another.";
          }
        } else {
          console.error('Invalid checkout response:', data);
        }
        
        setErrorMessage(errorMsg);
        toast({
          title: "Checkout Error",
          description: errorMsg,
          variant: "destructive",
        });
        return;
      }

      // Redirect to checkout
      window.location.href = data.url;
    } catch (error: any) {
      console.error('Checkout error:', error);
      const errorMsg = error.message || "Unable to process donation";
      setErrorMessage(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-xs mb-3">
          {errorMessage}
        </div>
      )}
      <Button 
        onClick={handleDonation}
        className={`flex items-center gap-2 ${isCompact ? 'text-xs md:text-sm' : ''}`}
        variant={variant}
        size={size}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className={`${isCompact ? 'w-3 h-3 md:w-4 md:h-4' : 'w-4 h-4 md:w-5 md:h-5'} animate-spin`} />
            {isCompact ? "Processing..." : "Processing Donation..."}
          </>
        ) : (
          <>
            <Gift className={isCompact ? 'w-3 h-3 md:w-4 md:h-4' : 'w-4 h-4 md:w-5 md:h-5'} />
            {isCompact ? "Donate" : "Donate Now"}
          </>
        )}
      </Button>
    </>
  );
}
